/**
 * Vincent B1028 (Eric 2026-02-26 SPEC STRICK PROD) — Menu déroulant unifié
 * « Sortir de la file... » qui remplace les 3 boutons de sortie affichés
 * côte à côte dans la File d'attente :
 *   1. Annuler (arrête le pipeline, garde la vidéo dans la file en cancelled).
 *   2. Retirer du pipeline (retire de la file, garde les données — B1025).
 *   3. Effacer (vidéo + données) (suppression DÉFINITIVE — B1025).
 *
 * Ordre volontaire par destructivité croissante (Annuler → Retirer → Effacer)
 * pour éviter les fat-fingers. Le 3ème item porte le flag `danger` pour
 * un traitement visuel distinct (rouge, séparé par une bordure du haut).
 */
import { Prohibit, XCircle, Trash } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

import ActionMenu from "./ActionMenu";


export default function ExitMenu({ video, busy, canCancel, canRemove, onCancel, onDetach, onErase }) {
  const { tr } = useLang();
  const items = [];

  // 1. Annuler — visible tant que le pipeline est en cours ou en pause.
  if (canCancel) {
    items.push({
      id: "cancel",
      label: tr("Annuler", "Cancel"),
      desc: tr(
        "Arrête le traitement en cours. La vidéo reste dans la file d'attente avec le statut « annulé ».",
        "Stops the running processing. The video stays in the queue with the \"cancelled\" status.",
      ),
      onClick: onCancel,
      busyKey: "cancel",
      icon: XCircle,
    });
  }

  // 2. Retirer du pipeline — non-destructif, réversible (B1025).
  if (canRemove) {
    items.push({
      id: "detach",
      label: tr("Retirer du pipeline", "Remove from the pipeline"),
      desc: tr(
        "Retire la vidéo de la file mais conserve toutes ses données. Réversible depuis la page de la vidéo.",
        "Takes the video out of the queue but keeps all of its data. Reversible from the video page.",
      ),
      onClick: onDetach,
      busyKey: "detach",
      icon: Prohibit,
    });
  }

  // 3. Effacer (vidéo + données) — IRRÉVERSIBLE. En bas + flag danger.
  if (canRemove) {
    items.push({
      id: "erase",
      label: tr("Effacer (vidéo + données)", "Delete (video + data)"),
      desc: tr(
        "Supprime DÉFINITIVEMENT la vidéo et toutes ses données (transcription, Q&R, scripts, procédures). Irréversible.",
        "PERMANENTLY deletes the video and all of its data (transcript, Q&A, scripts, procedures). Irreversible.",
      ),
      onClick: onErase,
      busyKey: "remove",
      icon: Trash,
      danger: true,
    });
  }

  return (
    <ActionMenu
      video={video}
      busy={busy}
      items={items}
      triggerLabel={tr("Sortir de la file", "Leave the queue")}
      triggerIcon={Prohibit}
      triggerClass="bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-100 hover:bg-zinc-300 dark:hover:bg-zinc-600"
      testidPrefix="queue-exit-menu"
    />
  );
}
